import type { DictFragment } from '../translations';

/**
 * Project submission / intake flow: Submit Project, Inbox (admin review +
 * change requests), and Council (steering-committee qualification & backlog).
 * Prefixes: `submitProject.*`, `inbox.*`, `council.*`.
 */
export const projectFlow: DictFragment = {
  fr: {
    // ── SubmitProjectPage ──
    'submitProject.sectionProblem': 'Problème ou besoin',
    'submitProject.problemLabel': 'Description du problème / besoin',
    'submitProject.problemPlaceholder': 'Quel problème ou besoin cette initiative adresse-t-elle ?',
    'submitProject.goalLabel': 'Objectif',
    'submitProject.goalPlaceholder': 'Quel est le résultat souhaité ?',
    'submitProject.valueAddedLabel': 'Valeur ajoutée',
    'submitProject.sectionDeliverable': 'Livrable',
    'submitProject.deliverableLabel': 'Ce qui existera à la fin',
    'submitProject.sectionResources': 'Ressources & effort (estimation)',
    'submitProject.effortLabel': 'Taille de l’effort (jours-homme)',
    'submitProject.timelineLabel': 'Échéance jusqu’à l’achèvement',
    'submitProject.cashLabel': 'Budget en numéraire',
    'submitProject.cashPlaceholder': 'ex. 50k, 200k EUR',
    'submitProject.sectionOwnership': 'Portage',
    'submitProject.sponsorLabel': 'Commanditaire',
    'submitProject.sponsorPlaceholder': 'Qui commandite cette initiative ?',
    'submitProject.userGroupsLabel': 'Utilisateurs / groupes d’utilisateurs',
    'submitProject.tagInputPlaceholder': 'Saisissez puis appuyez sur Entrée',
    'submitProject.submittedTitle': 'Initiative soumise',
    'submitProject.submittedDesc': 'Votre initiative a été soumise pour révision. Vous pouvez la retrouver dans l’onglet « Mes soumissions ».',
    'submitProject.submitAnother': 'Soumettre une autre',
    'submitProject.backToSubmissions': 'Retour aux soumissions',
    'submitProject.editTitle': 'Modifier l’initiative',
    'submitProject.namePlaceholder': 'Nom de l’initiative',
    'submitProject.saving': 'Enregistrement…',
    'submitProject.saveChanges': 'Enregistrer les modifications',
    'submitProject.tabNew': 'Nouvelle soumission',
    'submitProject.tabMySubmissions': 'Mes soumissions',
    'submitProject.draftRestored': 'Brouillon restauré',
    'submitProject.discard': 'Ignorer',
    'submitProject.submitTitle': 'Soumettre une initiative',
    'submitProject.submitIntro': 'Proposez une nouvelle initiative pour révision. Renseignez autant de détails que possible.',
    'submitProject.draftSaved': 'Brouillon enregistré',
    'submitProject.submitting': 'Envoi…',
    'submitProject.submitForReview': 'Soumettre pour révision',
    'submitProject.inInbox': 'Dans la boîte de réception',
    'submitProject.sectionsFilled': '{filled}/6 sections remplies',
    'submitProject.noSubmissionsTitle': 'Aucune soumission en attente',
    'submitProject.noSubmissionsDesc': 'Les soumissions en attente de révision apparaîtront ici.',

    // ── InboxPage ──
    'inbox.directionBanner': 'Vous proposez des modifications. Un administrateur examinera votre demande.',
    'inbox.changeRequestSuccess': 'Demande de modification soumise avec succès. Un administrateur examinera votre demande.',
    'inbox.changeRequestFailed': 'Échec de l’envoi de la demande de modification',
    'inbox.backToInbox': 'Retour à la boîte de réception',
    'inbox.sectionInitiative': 'Initiative',
    'inbox.namePlaceholder': 'Nom de l’initiative',
    'inbox.tabOverview': 'Aperçu',
    'inbox.tabPrequalification': 'Préqualification',
    'inbox.tabScoping': 'Scoping',
    'inbox.tabQualification': 'Qualification',
    'inbox.tabPlanning': 'Planification',
    'inbox.tabExecution': 'Exécution',
    'inbox.tabHandover': 'Transfert',
    'inbox.statusLabel': 'Statut',
    'inbox.domainLabel': 'Domaine du portefeuille',
    'inbox.selectDomain': 'Sélectionner un domaine',
    'inbox.levelLabel': 'Niveau stratégique',
    'inbox.selectLevel': 'Sélectionner un niveau',
    'inbox.sectionProblem': 'Problème ou besoin',
    'inbox.problemLabel': 'Description du problème / besoin',
    'inbox.problemPlaceholder': 'Quel problème ou besoin cette initiative adresse-t-elle ?',
    'inbox.goalLabel': 'Objectif',
    'inbox.goalPlaceholder': 'Quel est le résultat souhaité ?',
    'inbox.valueAddedLabel': 'Valeur ajoutée',
    'inbox.sectionDeliverable': 'Livrable',
    'inbox.deliverableLabel': 'Ce qui existera à la fin',
    'inbox.sectionResources': 'Ressources & effort (estimation)',
    'inbox.effortLabel': 'Taille de l’effort (jours-homme)',
    'inbox.timelineLabel': 'Échéance jusqu’à l’achèvement',
    'inbox.cashLabel': 'Budget en numéraire',
    'inbox.cashPlaceholder': 'ex. 50k, 200k EUR',
    'inbox.sectionOwnership': 'Portage',
    'inbox.sponsorLabel': 'Commanditaire',
    'inbox.sponsorPlaceholder': 'Qui commandite cette initiative ?',
    'inbox.userGroupsLabel': 'Utilisateurs / groupes d’utilisateurs',
    'inbox.tagInputPlaceholder': 'Saisissez puis appuyez sur Entrée',
    'inbox.qualificationPlaceholder': 'Les détails de qualification seront disponibles ici dans une prochaine mise à jour.',
    'inbox.executionPlaceholder': 'Le suivi de l’exécution sera disponible ici dans une prochaine mise à jour.',
    'inbox.delete': 'Supprimer',
    'inbox.requestChange': 'Demander une modification',
    'inbox.saveChanges': 'Enregistrer les modifications',
    'inbox.createInitiative': 'Créer une initiative',
    'inbox.deleteInitiativeTitle': 'Supprimer l’initiative',
    'inbox.deleteInitiativeMessage': 'Supprimer cette initiative ? Cette action est irréversible.',
    'inbox.myChangeRequests': 'Mes demandes de modification',
    'inbox.pendingCount': '{n} en attente',
    'inbox.statusAll': 'Toutes',
    'inbox.statusPending': 'En attente',
    'inbox.statusApproved': 'Approuvée',
    'inbox.statusRejected': 'Rejetée',
    'inbox.noChangeRequestsTitle': 'Aucune demande de modification',
    'inbox.noChangeRequestsDescUser': 'Cliquez sur le nom d’un projet dans Portefeuille ou Pipeline pour proposer des modifications.',
    'inbox.noChangeRequestsDescAdmin': 'Les demandes de modification des utilisateurs Direction apparaîtront ici.',
    'inbox.tabIncoming': 'Projets entrants',
    'inbox.tabChanges': 'Demandes de modification',
    'inbox.awaitingReviewOne': '{n} initiative en attente de révision',
    'inbox.awaitingReviewMany': '{n} initiatives en attente de révision',
    'inbox.newInitiative': 'Nouvelle initiative',
    'inbox.newNameLabel': "Nom de l'initiative",
    'inbox.newNamePlaceholder': "Un nom court qui dit de quoi il s'agit",
    'inbox.newHelp': "L'initiative est créée dans la boîte de réception et s'ouvre sur sa fiche projet, où l'onglet « À faire · Demande » liste ce qu'il reste à décrire.",
    'inbox.createAndOpen': "Créer et ouvrir",
    'inbox.createFailed': "La création a échoué.",
    'inbox.deleteTitle': 'Supprimer',
    'inbox.sectionsFilled': '{filled}/6 sections remplies',
    'inbox.postedOn': 'Postée le {date}',
    'inbox.postedOnBy': 'Postée le {date} par {name}',
    'inbox.noInitiativesTitle': 'Aucune initiative dans la boîte de réception',
    'inbox.noInitiativesDesc': 'Créez votre première initiative pour démarrer le processus d’admission.',
    'inbox.byRequester': 'par {name}',
    'inbox.quotedComment': '« {comment} »',

    // ── CouncilPage ──
    'council.title': 'Conseil de Pilotage',
    'council.subtitle': 'Conseil de qualification & révision du backlog / différé',
    'council.tabQualification': 'Qualification ({n})',
    'council.tabBacklog': 'Backlog & Différé',
    'council.noQualificationTitle': 'Aucun projet en attente de qualification',
    'council.noQualificationDesc': 'Les projets en Qualification apparaîtront ici pour révision par le conseil.',
    'council.promoteToPlanning': 'Promouvoir en Planification',
    'council.moveToBacklog': 'Déplacer vers le backlog',
    'council.backlogHeading': 'Backlog ({n})',
    'council.noBacklog': 'Aucun projet dans le backlog.',
    'council.deferredToReviewHeading': 'Différé — à réviser ({n})',
    'council.noDeferredToReview': 'Aucun projet différé à réviser.',
    'council.markReviewed': 'Marquer comme révisé',
    'council.deferredReviewedHeading': 'Différé — révisé ({n})',
    'council.noDeferredReviewed': 'Aucun projet différé révisé.',
    'council.markToReview': 'Marquer à réviser',
    'council.deferredOn': 'Différé le {date}',
    'council.reviewedOn': 'Révisé le {date}',
  },
  en: {
    // ── SubmitProjectPage ──
    'submitProject.sectionProblem': 'Problem or Need',
    'submitProject.problemLabel': 'Problem / Need Description',
    'submitProject.problemPlaceholder': 'What problem or need does this initiative address?',
    'submitProject.goalLabel': 'Goal',
    'submitProject.goalPlaceholder': 'What is the desired outcome?',
    'submitProject.valueAddedLabel': 'Value Added',
    'submitProject.sectionDeliverable': 'Deliverable',
    'submitProject.deliverableLabel': 'What will exist at the end',
    'submitProject.sectionResources': 'Resources & Effort (Estimate)',
    'submitProject.effortLabel': 'Effort Size (Man-Days)',
    'submitProject.timelineLabel': 'Timeline Until Completed',
    'submitProject.cashLabel': 'Cash Budget',
    'submitProject.cashPlaceholder': 'e.g. 50k, 200k EUR',
    'submitProject.sectionOwnership': 'Ownership',
    'submitProject.sponsorLabel': 'Sponsor',
    'submitProject.sponsorPlaceholder': 'Who sponsors this?',
    'submitProject.userGroupsLabel': 'User / User Groups',
    'submitProject.tagInputPlaceholder': 'Type and press Enter',
    'submitProject.submittedTitle': 'Initiative Submitted',
    'submitProject.submittedDesc': 'Your initiative has been submitted for review. You can find it in the "My Submissions" tab.',
    'submitProject.submitAnother': 'Submit Another',
    'submitProject.backToSubmissions': 'Back to submissions',
    'submitProject.editTitle': 'Edit Initiative',
    'submitProject.namePlaceholder': 'Initiative name',
    'submitProject.saving': 'Saving...',
    'submitProject.saveChanges': 'Save Changes',
    'submitProject.tabNew': 'New Submission',
    'submitProject.tabMySubmissions': 'My Submissions',
    'submitProject.draftRestored': 'Draft restored',
    'submitProject.discard': 'Discard',
    'submitProject.submitTitle': 'Submit Initiative',
    'submitProject.submitIntro': 'Propose a new initiative for review. Fill in as much detail as you can.',
    'submitProject.draftSaved': 'Draft saved',
    'submitProject.submitting': 'Submitting...',
    'submitProject.submitForReview': 'Submit for Review',
    'submitProject.inInbox': 'In Inbox',
    'submitProject.sectionsFilled': '{filled}/6 sections filled',
    'submitProject.noSubmissionsTitle': 'No pending submissions',
    'submitProject.noSubmissionsDesc': 'Submissions awaiting review will appear here.',

    // ── InboxPage ──
    'inbox.directionBanner': 'You are proposing changes. An admin will review your request.',
    'inbox.changeRequestSuccess': 'Change request submitted successfully. An admin will review your request.',
    'inbox.changeRequestFailed': 'Failed to submit change request',
    'inbox.backToInbox': 'Back to Inbox',
    'inbox.sectionInitiative': 'Initiative',
    'inbox.namePlaceholder': 'Initiative name',
    'inbox.tabOverview': 'Overview',
    'inbox.tabPrequalification': 'Prequalification',
    'inbox.tabScoping': 'Scoping',
    'inbox.tabQualification': 'Qualification',
    'inbox.tabPlanning': 'Planning',
    'inbox.tabExecution': 'Execution',
    'inbox.tabHandover': 'Handover',
    'inbox.statusLabel': 'Status',
    'inbox.domainLabel': 'Portfolio Domain',
    'inbox.selectDomain': 'Select domain',
    'inbox.levelLabel': 'Strategic Level',
    'inbox.selectLevel': 'Select level',
    'inbox.sectionProblem': 'Problem or Need',
    'inbox.problemLabel': 'Problem / Need Description',
    'inbox.problemPlaceholder': 'What problem or need does this initiative address?',
    'inbox.goalLabel': 'Goal',
    'inbox.goalPlaceholder': 'What is the desired outcome?',
    'inbox.valueAddedLabel': 'Value Added',
    'inbox.sectionDeliverable': 'Deliverable',
    'inbox.deliverableLabel': 'What will exist at the end',
    'inbox.sectionResources': 'Resources & Effort (Estimate)',
    'inbox.effortLabel': 'Effort Size (Man-Days)',
    'inbox.timelineLabel': 'Timeline Until Completed',
    'inbox.cashLabel': 'Cash Budget',
    'inbox.cashPlaceholder': 'e.g. 50k, 200k EUR',
    'inbox.sectionOwnership': 'Ownership',
    'inbox.sponsorLabel': 'Sponsor',
    'inbox.sponsorPlaceholder': 'Who sponsors this?',
    'inbox.userGroupsLabel': 'User / User Groups',
    'inbox.tagInputPlaceholder': 'Type and press Enter',
    'inbox.qualificationPlaceholder': 'Qualification details will be available here in a future update.',
    'inbox.executionPlaceholder': 'Execution tracking will be available here in a future update.',
    'inbox.delete': 'Delete',
    'inbox.requestChange': 'Request Change',
    'inbox.saveChanges': 'Save Changes',
    'inbox.createInitiative': 'Create Initiative',
    'inbox.deleteInitiativeTitle': 'Delete Initiative',
    'inbox.deleteInitiativeMessage': 'Delete this initiative? This cannot be undone.',
    'inbox.myChangeRequests': 'My Change Requests',
    'inbox.pendingCount': '{n} pending',
    'inbox.statusAll': 'All',
    'inbox.statusPending': 'Pending',
    'inbox.statusApproved': 'Approved',
    'inbox.statusRejected': 'Rejected',
    'inbox.noChangeRequestsTitle': 'No change requests',
    'inbox.noChangeRequestsDescUser': 'Click on a project name in Portfolio or Pipeline to propose changes.',
    'inbox.noChangeRequestsDescAdmin': 'Change requests from direction users will appear here.',
    'inbox.tabIncoming': 'Incoming Projects',
    'inbox.tabChanges': 'Change Requests',
    'inbox.awaitingReviewOne': '{n} initiative awaiting review',
    'inbox.awaitingReviewMany': '{n} initiatives awaiting review',
    'inbox.newInitiative': 'New Initiative',
    'inbox.newNameLabel': "Initiative name",
    'inbox.newNamePlaceholder': "A short name that says what it is about",
    'inbox.newHelp': "The initiative is created in the inbox and opens on its project page, where the “To do · Request” tab lists what is left to describe.",
    'inbox.createAndOpen': "Create and open",
    'inbox.createFailed': "Creation failed.",
    'inbox.deleteTitle': 'Delete',
    'inbox.sectionsFilled': '{filled}/6 sections filled',
    'inbox.postedOn': 'Posted on {date}',
    'inbox.postedOnBy': 'Posted on {date} by {name}',
    'inbox.noInitiativesTitle': 'No initiatives in inbox',
    'inbox.noInitiativesDesc': 'Create your first initiative to start the intake process.',
    'inbox.byRequester': 'by {name}',
    'inbox.quotedComment': '"{comment}"',

    // ── CouncilPage ──
    'council.title': 'Steering Committee',
    'council.subtitle': 'Qualification council & backlog / deferred review',
    'council.tabQualification': 'Qualification ({n})',
    'council.tabBacklog': 'Backlog & Deferred',
    'council.noQualificationTitle': 'No projects awaiting qualification',
    'council.noQualificationDesc': 'Projects in Qualification will appear here for council review.',
    'council.promoteToPlanning': 'Promote to Planning',
    'council.moveToBacklog': 'Move to Backlog',
    'council.backlogHeading': 'Backlog ({n})',
    'council.noBacklog': 'No projects in backlog.',
    'council.deferredToReviewHeading': 'Deferred — To review ({n})',
    'council.noDeferredToReview': 'No deferred projects to review.',
    'council.markReviewed': 'Mark reviewed',
    'council.deferredReviewedHeading': 'Deferred — Reviewed ({n})',
    'council.noDeferredReviewed': 'No reviewed deferred projects.',
    'council.markToReview': 'Mark to review',
    'council.deferredOn': 'Deferred {date}',
    'council.reviewedOn': 'Reviewed {date}',
  },
};
